// Ricerca globale Cmd+K con risultati rapidi
import { useState, useEffect, useRef, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { Search, X, Ticket, FileText, Users, Building2, Package } from 'lucide-react'
import { cn, debounce } from '../../lib/utils'
import api from '../../lib/api'

interface SearchResult {
  id: string
  type: 'ticket' | 'report' | 'technician' | 'company' | 'material'
  title: string
  subtitle?: string
  href: string
}

const TYPE_ICONS: Record<SearchResult['type'], React.ReactNode> = {
  ticket: <Ticket className="h-4 w-4 text-[var(--fc-primary)]" />,
  report: <FileText className="h-4 w-4 text-[var(--fc-info)]" />,
  technician: <Users className="h-4 w-4 text-[var(--fc-success)]" />,
  company: <Building2 className="h-4 w-4 text-[var(--fc-warning)]" />,
  material: <Package className="h-4 w-4 text-[var(--fc-accent)]" />,
}

const TYPE_LABELS: Record<SearchResult['type'], string> = {
  ticket: 'Ticket',
  report: 'Rapportino',
  technician: 'Tecnico',
  company: 'Cliente',
  material: 'Materiale',
}

interface GlobalSearchProps {
  open: boolean
  onClose: () => void
}

export function GlobalSearch({ open, onClose }: GlobalSearchProps) {
  const navigate = useNavigate()
  const [query, setQuery] = useState('')
  const [results, setResults] = useState<SearchResult[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [selectedIndex, setSelectedIndex] = useState(0)
  const inputRef = useRef<HTMLInputElement>(null)

  const performSearch = useCallback(
    debounce(async (q: string) => {
      if (q.length < 2) {
        setResults([])
        return
      }
      setIsLoading(true)
      try {
        const response = await api.get<SearchResult[]>('/search', { params: { q } })
        setResults(response.data)
      } catch {
        setResults([])
      } finally {
        setIsLoading(false)
      }
    }, 300),
    []
  )

  useEffect(() => {
    if (open) {
      setQuery('')
      setResults([])
      setTimeout(() => inputRef.current?.focus(), 50)
    }
  }, [open])

  useEffect(() => {
    void performSearch(query)
    setSelectedIndex(0)
  }, [query, performSearch])

  const handleSelect = (result: SearchResult) => {
    navigate(result.href)
    onClose()
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setSelectedIndex((i) => Math.min(i + 1, results.length - 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setSelectedIndex((i) => Math.max(i - 1, 0))
    } else if (e.key === 'Enter' && results[selectedIndex]) {
      handleSelect(results[selectedIndex]!)
    } else if (e.key === 'Escape') {
      onClose()
    }
  }

  if (!open) return null

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-[15vh]">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50" onClick={onClose} aria-hidden="true" />

      {/* Dialog */}
      <div
        className="relative w-full max-w-lg rounded-xl border border-[var(--fc-border)] bg-[var(--fc-surface)] shadow-[var(--fc-shadow-lg)] overflow-hidden"
        role="dialog"
        aria-label="Ricerca globale"
        aria-modal="true"
      >
        {/* Campo ricerca */}
        <div className="flex items-center gap-3 border-b border-[var(--fc-border)] px-4 py-3">
          <Search className="h-5 w-5 shrink-0 text-[var(--fc-foreground-muted)]" aria-hidden="true" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Cerca ticket, tecnici, clienti..."
            className="flex-1 bg-transparent text-sm text-[var(--fc-foreground)] placeholder:text-[var(--fc-foreground-muted)] outline-none"
            role="combobox"
            aria-expanded={results.length > 0}
            aria-autocomplete="list"
            aria-controls="search-results"
          />
          {query && (
            <button
              onClick={() => setQuery('')}
              className="text-[var(--fc-foreground-muted)] hover:text-[var(--fc-foreground)] transition-colors"
              aria-label="Cancella ricerca"
            >
              <X className="h-4 w-4" />
            </button>
          )}
          <button
            onClick={onClose}
            className="rounded border border-[var(--fc-border)] px-1.5 py-0.5 text-xs text-[var(--fc-foreground-muted)]"
            aria-label="Chiudi (Esc)"
          >
            Esc
          </button>
        </div>

        {/* Risultati */}
        <div className="max-h-96 overflow-y-auto" id="search-results" role="listbox">
          {isLoading && (
            <div className="flex items-center justify-center py-6">
              <div className="h-5 w-5 animate-spin rounded-full border-2 border-[var(--fc-primary)] border-t-transparent" />
            </div>
          )}

          {!isLoading && query.length >= 2 && results.length === 0 && (
            <div className="py-8 text-center text-sm text-[var(--fc-foreground-muted)]">
              Nessun risultato per "{query}"
            </div>
          )}

          {!isLoading && results.length > 0 && (
            <ul>
              {results.map((result, index) => (
                <li key={result.id} role="option" aria-selected={index === selectedIndex}>
                  <button
                    className={cn(
                      'flex w-full items-center gap-3 px-4 py-3 text-left transition-colors',
                      index === selectedIndex
                        ? 'bg-[var(--fc-surface-overlay)]'
                        : 'hover:bg-[var(--fc-surface-raised)]'
                    )}
                    onClick={() => handleSelect(result)}
                  >
                    <span className="shrink-0">{TYPE_ICONS[result.type]}</span>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium text-[var(--fc-foreground)]">
                        {result.title}
                      </p>
                      {result.subtitle && (
                        <p className="truncate text-xs text-[var(--fc-foreground-muted)]">
                          {result.subtitle}
                        </p>
                      )}
                    </div>
                    <span className="shrink-0 rounded bg-[var(--fc-surface-overlay)] px-1.5 py-0.5 text-xs text-[var(--fc-foreground-muted)]">
                      {TYPE_LABELS[result.type]}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}

          {!query && (
            <div className="px-4 py-6 text-center text-sm text-[var(--fc-foreground-muted)]">
              Digita per cercare in KleisNote
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
